import { TaskVideoLibrary } from "./task-video";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Alert, Button, Card, Empty, Image, Input, Modal, Pagination, Progress, Segmented, Select, Space, Spin, Tag, Typography, Upload, theme } from "antd";
import { CloudUploadOutlined, CheckOutlined, FolderOpenOutlined, PictureOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { api, query, upload } from "./api";
import { dateTime } from "./domain";
import { SectionTitle } from "./shared";
import { FileDimensions, ImageDimensions, imageSizeText } from "./image-dimensions";
import "./materials.css";

export type MaterialModule = "task_icon" | "task_step";
export interface Material {
  id: number;
  agent_id: number;
  name: string;
  module: MaterialModule;
  object_key: string;
  url: string;
  mime: string;
  size: number;
  width: number;
  height: number;
  created_at: string;
}
interface MaterialPage { items: Material[]; total: number; page: number; page_size: number }
const moduleLabels: Record<MaterialModule, string> = { task_icon: "任务图标", task_step: "流程图" };
const moduleGuidance: Record<MaterialModule, string> = {
  task_icon: "推荐尺寸：512 × 512 px（宽 × 高，1:1 正方形）。",
  task_step: "尺寸要求：不固定宽高，建议保留原始截图比例，确保步骤文字清晰。",
};
const assetCache = new Map<string, string>();
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PIXELS = 24_000_000;
const accept = "image/png,image/jpeg,image/gif,image/webp";
function safeImageURL(value?: string) {
  if (!value) return undefined;
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password ? url.href : undefined; }
  catch { return undefined; }
}
export function MaterialImage({ value, assetURLs, width = 80, alt = "任务素材" }: {
  value?: string; assetURLs?: Record<string, string>; width?: number; alt?: string;
}) {
  const src = safeImageURL(value && (assetURLs?.[value] || assetCache.get(value) || value));
  return src ? <Image src={src} alt={alt} width={width} style={{ maxHeight: width * 1.4, objectFit: "contain" }} />
    : <span className="material-image-placeholder" style={{ width, minHeight: width }}><PictureOutlined /><small>{value ? "预览暂不可用" : "未选择图片"}</small></span>;
}

/** Early feedback only; the server repeats real-content decoding and size checks. */
export async function validateMaterial(file: File): Promise<void> {
  if (!file.size) throw new Error("图片为空，请重新选择");
  if (file.size > MAX_BYTES) throw new Error("图片不能超过 10 MiB，请压缩后重试");
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const text = new TextDecoder("ascii").decode(bytes);
  const png = bytes[0] === 0x89 && text.slice(1, 4) === "PNG" && bytes[4] === 13 && bytes[5] === 10 && bytes[6] === 26 && bytes[7] === 10;
  const jpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const gif = text.startsWith("GIF87a") || text.startsWith("GIF89a");
  const webp = text.startsWith("RIFF") && text.slice(8, 12) === "WEBP";
  if (!(png || jpeg || gif || webp)) throw new Error("仅支持真实的 PNG、JPEG、GIF、WebP 图片，不支持 SVG 或修改后缀的文件");
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => {
        if (!image.naturalWidth || !image.naturalHeight) reject(new Error("图片尺寸无效，请重新选择"));
        else if (image.naturalWidth * image.naturalHeight > MAX_PIXELS) reject(new Error("图片像素不能超过 2400 万，请缩小图片尺寸"));
        else resolve();
      };
      image.onerror = () => reject(new Error("无法读取图片，图片可能已损坏，请重新选择"));
      image.src = url;
    });
  } finally { URL.revokeObjectURL(url); }
}

function MaterialDropzone({ module, disabled, onChoose, file }: {
  module: MaterialModule; disabled?: boolean; onChoose: (file: File) => void; file?: File;
}) {
  const { token } = theme.useToken();
  return <div className="material-dropzone" style={{
    "--material-accent": token.colorPrimary, "--material-tint": token.colorPrimaryBg,
    "--material-line": token.colorBorderSecondary, "--material-bg": token.colorBgContainer,
    "--material-muted": token.colorTextSecondary,
  } as CSSProperties}><Upload.Dragger accept={accept} multiple={false} maxCount={1} showUploadList={false} disabled={disabled}
    beforeUpload={file => { onChoose(file); return false; }}>
    <span className="material-dropzone-icon"><CloudUploadOutlined /></span>
    <strong>{file ? "点击或拖拽替换图片" : "点击上传或将图片拖到这里"}</strong>
    <p>{moduleGuidance[module]}</p>
    <span className="material-dropzone-formats">PNG / JPEG / GIF / WebP · 最大 10 MiB、2400 万像素</span>
  </Upload.Dragger></div>;
}

function MaterialUpload({ module, onUploaded, disabled = false, dropzone = false }: {
  module: MaterialModule; onUploaded: (item: Material) => void; disabled?: boolean; dropzone?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File>();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [target, setTarget] = useState(module);
  const abortRef = useRef<AbortController | null>(null);
  const choose = (selected: File) => { setFile(selected); setName(selected.name.slice(0, 120)); setError(""); setProgress(0); };
  useEffect(() => () => abortRef.current?.abort(), []);
  const send = async () => {
    if (!file || busy || disabled) return;
    setError(""); setBusy(true); setProgress(0); setPhase("正在检查图片…");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await validateMaterial(file);
      if (controller.signal.aborted) return;
      const form = new FormData();
      form.append("file", file);
      form.append("module", target);
      form.append("name", name.trim() || file.name);
      setPhase("正在上传图片…");
      const result = await upload<{ item: Material }>("/materials/upload", form, (percent) => {
        setProgress(percent);
        setPhase(percent === 100 ? "图片已传输，正在等待存储确认…" : "正在上传图片…");
      }, controller.signal);
      if (!result.item?.object_key || !safeImageURL(result.item.url)) throw new Error("服务未返回有效素材，请刷新素材列表确认上传结果");
      assetCache.set(result.item.object_key, result.item.url);
      onUploaded(result.item);
      setOpen(false); setFile(undefined); setName("");
    } catch (e) {
      if (!controller.signal.aborted) setError((e as Error).message);
    } finally {
      if (abortRef.current === controller) { setBusy(false); abortRef.current = null; }
    }
  };
  return <>
    {dropzone ? <MaterialDropzone module={module} disabled={disabled} onChoose={selected => { choose(selected); setTarget(module); setOpen(true); }} /> : <Button type="primary" icon={<UploadOutlined />} disabled={disabled} onClick={() => {
      setTarget(module); setError(""); setProgress(0); setOpen(true);
    }}>上传图片</Button>}
    <Modal title="上传素材" open={open} onCancel={() => setOpen(false)} closable={!busy} maskClosable={!busy} keyboard={!busy}
      footer={<Space><Button disabled={busy} onClick={() => setOpen(false)}>取消</Button><Button type="primary" loading={busy} disabled={!file || disabled} onClick={() => void send()}>开始上传</Button></Space>}>
      <div className="material-upload-form">
        <label>素材分类<Select value={target} disabled options={[{ value: target, label: moduleLabels[target] }]} /></label>
        <label>素材名称<Input value={name} maxLength={120} disabled={busy} placeholder="默认使用文件名" onChange={(event) => setName(event.target.value)} /></label>
        <MaterialDropzone module={target} disabled={busy || disabled} onChoose={choose} file={file} />
        {file && <Typography.Text className="material-filename">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MiB</Typography.Text>}
        <FileDimensions file={file} />
        <Typography.Text type="secondary">{moduleGuidance[target]}</Typography.Text>
        <Typography.Text type="secondary">支持 PNG、JPEG、GIF、WebP；每张不超过 10 MiB、2400 万像素。上传后可在任务中重复选用。</Typography.Text>
        {busy && <div role="status" aria-live="polite"><Progress percent={progress} status="active" /><Typography.Text type="secondary">{phase}</Typography.Text></div>}
        {error && <Alert type="error" showIcon message="上传未完成" description={error} />}
      </div>
    </Modal>
  </>;
}

function MaterialLibrary({ fixedModule, value, onSelect, disabled = false }: {
  fixedModule?: MaterialModule; value?: string; onSelect?: (item: Material) => void; disabled?: boolean;
}) {
  const [module, setModule] = useState<MaterialModule>(fixedModule || "task_icon");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<MaterialPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const selectedModule = fixedModule || module;
  useEffect(() => {
    let active = true;
    setLoading(true); setError(""); setData(undefined);
    void api<MaterialPage>(`/materials${query({ module: selectedModule, search, page, page_size: pageSize })}`).then((result) => {
      if (!active) return;
      result.items.forEach((item) => assetCache.set(item.object_key, item.url));
      setData(result);
    }).catch((e: Error) => { if (active) setError(e.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedModule, search, page, pageSize, refresh]);
  const uploaded = (item: Material) => {
    setPage(1); setSearch(""); setSearchInput(""); setRefresh((n) => n + 1);
    onSelect?.(item);
  };
  return <div className="material-library">
    <div className="material-toolbar">
      {fixedModule ? <Tag color="blue">{moduleLabels[fixedModule]}</Tag> : <Segmented value={module} options={Object.entries(moduleLabels).map(([value, label]) => ({ value, label }))}
        onChange={(value) => { setModule(value as MaterialModule); setPage(1); }} />}
      <Space wrap><Input.Search allowClear value={searchInput} placeholder="搜索素材名称" aria-label="搜索素材名称" style={{ width: 240 }} onChange={(event) => setSearchInput(event.target.value)} onSearch={(value) => { setSearch(value.trim()); setPage(1); }} />
        <Button icon={<ReloadOutlined />} aria-label="刷新素材" onClick={() => setRefresh((n) => n + 1)} />
        <MaterialUpload module={selectedModule} onUploaded={uploaded} disabled={disabled} />
      </Space>
    </div>
    {error ? <Alert type="error" showIcon message="素材加载失败" description={error} action={<Button onClick={() => setRefresh((n) => n + 1)}>重试</Button>} /> : <Spin spinning={loading} tip="正在加载素材…">
      <div className="material-content">
        {!loading && !data?.items.length ? <Empty description={search ? "未找到匹配的素材，请更换关键词" : `暂无${moduleLabels[selectedModule]}，上传图片后即可选用`} /> : <div className="material-grid">
          {data?.items.map((item) => <Card key={item.id} size="small" className={`material-card ${value === item.object_key ? "is-selected" : ""}`}>
            <div className="material-cover"><MaterialImage value={item.object_key} width={144} alt={item.name} /></div>
            <Typography.Text ellipsis={{ tooltip: item.name }} strong className="material-name">{item.name}</Typography.Text>
            <div className="material-meta">{imageSizeText(item.width, item.height)} · {(item.size / 1024).toFixed(0)} KB</div>
            <div className="material-meta">{dateTime(item.created_at)}</div>
            {onSelect && <Button block type={value === item.object_key ? "primary" : "default"} icon={value === item.object_key ? <CheckOutlined /> : undefined} disabled={disabled} onClick={() => onSelect(item)}>{value === item.object_key ? "已选择" : "使用此图片"}</Button>}
          </Card>)}
        </div>}
      </div>
    </Spin>}
    {!error && <Pagination className="material-pagination" current={page} pageSize={pageSize} total={data?.total || 0} showSizeChanger pageSizeOptions={[20, 40, 80]} showTotal={(total) => `共 ${total} 张素材`} disabled={loading}
      onChange={(nextPage, nextSize) => { setPage(nextSize === pageSize ? nextPage : 1); setPageSize(nextSize); }} />}
  </div>;
}

export function MaterialsPage({ disabled = false }: { disabled?: boolean }) {
  return <><SectionTitle eyebrow="MATERIAL LIBRARY" title="App推广素材" description="管理推广任务图标与流程图，上传一次，在推广任务中重复使用。" /><Alert type="info" showIcon message="图片尺寸说明" description={<><p>任务图标：{moduleGuidance.task_icon}</p><p>流程图：{moduleGuidance.task_step}</p><p>支持 PNG / JPEG / GIF / WebP，单张最大 10 MiB、2400 万像素；推荐尺寸不作为强制限制。</p></>} style={{ marginBottom: 20 }} />{disabled && <Alert type="warning" showIcon message="当前业务已冻结，暂时无法上传素材" style={{ marginBottom: 20 }} />}<Card><MaterialLibrary disabled={disabled} /></Card>{!disabled && <TaskVideoLibrary />}</>;
}
export function MaterialPicker({ module, value, onChange, assetURLs, disabled = false, label }: {
  module: MaterialModule; value?: string; onChange?: (objectKey: string, material?: Material) => void;
  assetURLs?: Record<string, string>; disabled?: boolean; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = (item: Material) => { assetCache.set(item.object_key, item.url); onChange?.(item.object_key, item); setOpen(false); };
  return <div className={`material-picker ${!value ? "material-picker-empty" : ""}`}>
    {value ? <MaterialImage value={value} assetURLs={assetURLs} alt={label || moduleLabels[module]} /> : <MaterialUpload module={module} onUploaded={selected} disabled={disabled} dropzone />}
    <div className="material-picker-actions"><Space wrap>
      <Button icon={<FolderOpenOutlined />} disabled={disabled} onClick={() => setOpen(true)}>从素材库选择</Button>
      {value && <MaterialUpload module={module} onUploaded={selected} disabled={disabled} />}
      {value && <Button type="text" disabled={disabled} onClick={() => onChange?.("")}>清除图片</Button>}
    </Space><Typography.Text type="secondary">{label || moduleLabels[module]} · 支持本地上传或复用已有素材</Typography.Text><Typography.Text type="secondary">{moduleGuidance[module]} 单张最大 10 MiB、2400 万像素。</Typography.Text><ImageDimensions src={safeImageURL(value && (assetURLs?.[value] || assetCache.get(value) || value))} /></div>
    <Modal title={`选择${label || moduleLabels[module]}`} width={1000} open={open} destroyOnHidden onCancel={() => setOpen(false)} footer={<Button onClick={() => setOpen(false)}>关闭</Button>}>
      <MaterialLibrary fixedModule={module} value={value} onSelect={selected} disabled={disabled} />
    </Modal>
  </div>;
}
